import { Link } from 'react-router-dom';
import type { Article } from '@/types';
import { getEnglishContent } from '@/types';
import { getCategoryById } from '@/data/categories';
import { optimizeImageUrl, getFallbackImage } from '@/lib/imageUtils';
import { Eyebrow } from './Eyebrow';
import { ArticleCard } from '@/components/home/ArticleCard';

interface MagazineGridProps {
  articles: Article[];
  featureLabel?: string;
}

/**
 * Asymmetric magazine layout: one large feature on the left + a stack of
 * smaller cards on the right, plus an even card row below. Falls back
 * gracefully when fewer than 4 articles are provided.
 */
export const MagazineGrid = ({ articles, featureLabel = 'Editor’s pick' }: MagazineGridProps) => {
  if (!articles || articles.length === 0) return null;

  const [feature, ...rest] = articles;
  const sideCards = rest.slice(0, 2);
  const bottomRow = rest.slice(2, 5);

  const featureContent = getEnglishContent(feature);
  const featureCategory = getCategoryById(feature.category)?.name || feature.category;

  return (
    <div className="space-y-12">
      {/* Feature row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Feature */}
        <Link
          to={`/article/${feature.slug}`}
          className="lg:col-span-7 group block editorial-zoom"
        >
          <div className="relative overflow-hidden mb-6" style={{ aspectRatio: '4/3' }}>
            <img
              src={optimizeImageUrl(feature.image, 1200)}
              srcSet={`${optimizeImageUrl(feature.image, 800)} 800w, ${optimizeImageUrl(feature.image, 1200)} 1200w, ${optimizeImageUrl(feature.image, 1600)} 1600w`}
              sizes="(max-width: 1024px) 100vw, 60vw"
              alt={featureContent.title}
              loading="lazy"
              decoding="async"
              width={1200}
              height={900}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).src = getFallbackImage(feature.category); }}
            />
            <span className="absolute top-5 left-5 px-3 py-1.5 bg-background/90 backdrop-blur-sm font-mono text-[9px] tracking-editorial uppercase text-brass">
              {featureLabel}
            </span>
          </div>
          <Eyebrow className="mb-3">{featureCategory}</Eyebrow>
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground group-hover:text-primary transition-colors duration-700 leading-[1.1] tracking-tight mb-4">
            {featureContent.title}
          </h3>
          <p className="text-base text-muted-foreground/90 leading-relaxed font-light max-w-2xl line-clamp-3">
            {featureContent.excerpt}
          </p>
        </Link>

        {/* Side stack */}
        <div className="lg:col-span-5 flex flex-col divide-y divide-border/50">
          {sideCards.map((article, idx) => {
            const c = getEnglishContent(article);
            const catName = getCategoryById(article.category)?.name || article.category;
            return (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className={`group grid grid-cols-3 gap-5 editorial-zoom ${idx === 0 ? 'pb-8' : 'pt-8'}`}
              >
                <div className="col-span-1 relative overflow-hidden" style={{ aspectRatio: '1/1' }}>
                  <img
                    src={optimizeImageUrl(article.image, 400)}
                    alt={c.title}
                    loading="lazy"
                    decoding="async"
                    width={400}
                    height={400}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = getFallbackImage(article.category); }}
                  />
                </div>
                <div className="col-span-2 flex flex-col justify-center">
                  <Eyebrow className="mb-2 text-[9px]">{catName}</Eyebrow>
                  <h4 className="font-display text-xl md:text-2xl font-medium text-foreground group-hover:text-primary transition-colors duration-700 leading-tight tracking-tight">
                    {c.title}
                  </h4>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Bottom row — uses standard ArticleCard for full info */}
      {bottomRow.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-4 border-t border-border/40">
          {bottomRow.map((article, index) => (
            <ArticleCard key={article.id} article={article} index={index} />
          ))}
        </div>
      )}
    </div>
  );
};